import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { ShoppingBag, Search, User, Menu, X, Heart, ChevronDown, Sparkles } from 'lucide-react';
import { PRIMARY_NAV, SHOP_CATEGORIES } from '@meshsmith/shared';
import { useCart } from '../../hooks/useCart';
import { useAuth } from '../../hooks/useAuth';
import { classNames } from '../../lib/format';
import { Logo } from '../Logo';
import { SearchOverlay } from './SearchOverlay';
import { CallUs } from '../CallUs';
import { useCommerce } from '../../hooks/useCommerce';

export function Header() {
  const { freeShippingOver } = useCommerce();
  const { itemCount } = useCart();
  const { user, isStaff } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [shopOpen, setShopOpen] = useState(false);
  const [mobileShopOpen, setMobileShopOpen] = useState(true);
  const [searchOpen, setSearchOpen] = useState(false);
  const [q, setQ] = useState('');
  const shopRef = useRef<HTMLDivElement>(null);

  // Close menus on route change
  useEffect(() => {
    setMobileOpen(false);
    setShopOpen(false);
    setSearchOpen(false);
  }, [location.pathname, location.search]);

  // Close the Shop dropdown on outside click / Escape
  useEffect(() => {
    if (!shopOpen) return;
    const onClick = (e: MouseEvent) => {
      if (shopRef.current && !shopRef.current.contains(e.target as Node)) setShopOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setShopOpen(false);
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [shopOpen]);

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (q.trim()) {
      navigate(`/search?q=${encodeURIComponent(q.trim())}`);
      setSearchOpen(false);
      setMobileOpen(false);
      setQ('');
    }
  };

  const shopActive = location.pathname.startsWith('/c/') || location.pathname === '/shop';

  return (
    <header className="sticky top-0 z-40">
      {/* gradient hairline */}
      <div className="h-0.5 w-full bg-brand-gradient" />

      {/* announcement bar */}
      <div className="bg-charcoal text-white">
        <div className="container-ms flex h-8 items-center justify-between text-[0.75rem]">
          <p className="mx-auto flex items-center gap-1.5 md:mx-0">
            <Sparkles size={13} className="text-logo-from" />
            {freeShippingOver ? `Free shipping over ${freeShippingOver} · Ships across India` : 'Ships across India'}
          </p>
          <div className="hidden items-center gap-5 text-white/80 md:flex">
            <CallUs variant="inline" />
            <nav className="hidden items-center gap-4 border-l border-white/20 pl-5 text-white/70 lg:flex">
              <Link to="/custom-orders" className="hover:text-white">
                Custom Orders
              </Link>
              <Link to="/faq" className="hover:text-white">
                Help
              </Link>
            </nav>
          </div>
        </div>
      </div>

      {/* main bar */}
      <div className="border-b bg-warm-off-white/90 backdrop-blur" style={{ borderColor: 'var(--border)' }}>
        <div className="container-ms flex h-16 items-center gap-3">
          <button
            className="btn-ghost -ml-2 rounded-lg p-2 lg:hidden"
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
          >
            <Menu size={22} />
          </button>

          <Logo markClassName="h-9 w-9 shrink-0" wordmarkClassName="text-[1.3rem] hidden sm:block" />

          {/* desktop nav */}
          <nav className="ml-3 hidden items-center gap-0.5 lg:flex" aria-label="Primary">
            <div
              ref={shopRef}
              className="relative"
              onMouseEnter={() => setShopOpen(true)}
              onMouseLeave={() => setShopOpen(false)}
            >
              <button
                className={classNames('nav-link flex items-center gap-1', shopActive && 'is-active')}
                aria-expanded={shopOpen}
                aria-haspopup="true"
                onClick={() => setShopOpen((v) => !v)}
              >
                Shop
                <ChevronDown size={14} className={classNames('transition-transform', shopOpen && 'rotate-180')} />
              </button>

              {/* hover bridge so the pointer can travel to the panel */}
              {shopOpen && <div className="absolute left-0 top-full h-2 w-[420px]" />}

              {shopOpen && (
                <div className="card absolute left-0 top-[calc(100%+0.5rem)] w-[420px] p-2 shadow-lg">
                  <ul className="grid grid-cols-1 gap-0.5">
                    {SHOP_CATEGORIES.map((c) => (
                      <li key={c.to}>
                        <Link
                          to={c.to}
                          className="flex items-start gap-3 rounded-lg px-3 py-2.5 transition-colors hover:bg-brand-tint"
                        >
                          <span
                            className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full"
                            style={{ background: 'var(--brand-gradient)' }}
                          />
                          <span>
                            <span className="block text-sm font-semibold text-charcoal">{c.label}</span>
                            <span className="block text-xs text-secondary-grey">{c.blurb}</span>
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {PRIMARY_NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) => classNames('nav-link', isActive && 'is-active')}
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          {/* actions */}
          <div className="ml-auto flex items-center gap-0.5">
            <button
              className="btn-ghost rounded-lg p-2"
              onClick={() => setSearchOpen((v) => !v)}
              aria-label="Search"
              aria-expanded={searchOpen}
            >
              <Search size={20} />
            </button>
            {user && (
              <Link to="/account/wishlist" className="btn-ghost hidden rounded-lg p-2 sm:inline-flex" aria-label="Wishlist">
                <Heart size={20} />
              </Link>
            )}
            <Link
              to={user ? '/account' : '/login'}
              className="btn-ghost rounded-lg p-2"
              aria-label={user ? 'Account' : 'Sign in'}
            >
              <User size={20} />
            </Link>
            {isStaff && (
              <Link
                to="/admin"
                className="hidden rounded-lg px-3 py-2 text-[0.82rem] font-semibold text-deep-teal hover:bg-pale-aqua lg:block"
              >
                Admin
              </Link>
            )}
            <Link to="/cart" className="btn-ghost relative ml-1 rounded-lg p-2" aria-label={`Cart, ${itemCount} items`}>
              <ShoppingBag size={20} />
              {itemCount > 0 && (
                <span
                  className="absolute -right-0.5 -top-0.5 grid h-4 min-w-4 place-items-center rounded-full px-1 text-[0.62rem] font-bold text-white"
                  style={{ background: 'var(--brand-gradient)' }}
                >
                  {itemCount}
                </span>
              )}
            </Link>
          </div>
        </div>
      </div>

      <SearchOverlay
        open={searchOpen}
        query={q}
        onQueryChange={setQ}
        onSubmit={submitSearch}
        onClose={() => setSearchOpen(false)}
      />

      {/* mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-charcoal/40" onClick={() => setMobileOpen(false)} />
          <div className="absolute left-0 top-0 flex h-full w-[86%] max-w-sm flex-col bg-warm-off-white shadow-lg">
            <div className="flex items-center justify-between border-b p-4" style={{ borderColor: 'var(--border)' }}>
              <Logo to={undefined} markClassName="h-8 w-8" wordmarkClassName="text-lg" />
              <button onClick={() => setMobileOpen(false)} aria-label="Close menu" className="btn-ghost rounded-lg p-1.5">
                <X size={22} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              <form onSubmit={submitSearch} className="mb-4">
                <div
                  className="flex items-center gap-2.5 rounded-full border bg-white px-4 py-2.5 focus-within:border-deep-teal"
                  style={{ borderColor: 'var(--border)' }}
                >
                  <Search size={16} className="shrink-0 text-secondary-grey" />
                  <input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Search products, SKUs, materials…"
                    className="w-full bg-transparent text-sm outline-none placeholder:text-secondary-grey"
                    aria-label="Search"
                  />
                </div>
              </form>

              <button
                className="flex w-full items-center justify-between rounded-lg px-3 py-3 text-left font-semibold hover:bg-brand-tint"
                onClick={() => setMobileShopOpen((v) => !v)}
                aria-expanded={mobileShopOpen}
              >
                Shop
                <ChevronDown size={16} className={classNames('transition-transform', mobileShopOpen && 'rotate-180')} />
              </button>
              {mobileShopOpen && (
                <div className="mb-1 ml-3 flex flex-col border-l pl-2" style={{ borderColor: 'var(--border)' }}>
                  {SHOP_CATEGORIES.map((c) => (
                    <NavLink
                      key={c.to}
                      to={c.to}
                      className="rounded-lg px-3 py-2.5 text-sm hover:bg-brand-tint"
                    >
                      {c.label}
                    </NavLink>
                  ))}
                </div>
              )}

              <nav className="flex flex-col">
                {PRIMARY_NAV.map((item) => (
                  <NavLink key={item.to} to={item.to} className="rounded-lg px-3 py-3 font-semibold hover:bg-brand-tint">
                    {item.label}
                  </NavLink>
                ))}
                <NavLink to="/contact" className="rounded-lg px-3 py-3 font-semibold hover:bg-brand-tint">
                  Contact
                </NavLink>
              </nav>
            </div>

            <div className="border-t p-4" style={{ borderColor: 'var(--border)' }}>
              <CallUs className="mb-3" />
              <Link
                to={user ? '/account' : '/login'}
                className="btn btn-outline w-full"
              >
                <User size={16} /> {user ? 'My account' : 'Sign in'}
              </Link>
              {isStaff && (
                <Link to="/admin" className="mt-2 block text-center text-sm font-semibold text-deep-teal">
                  Admin panel
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
